import React from 'react';
import { Link } from 'react-router-dom';
import SatisfindLogo from '@/components/SatisfindLogo';

/**
 * Public marketing homepage at `/`.
 *
 * Editorial-serif hero + generous whitespace, matching the provided
 * style-reference screenshot. Uses Playfair Display (loaded via
 * index.html) with a Georgia/serif fallback so the page renders even
 * before the web-font is ready.
 *
 * Design constraints (from brief):
 *   • Headline: large, dark navy, serif, tight leading
 *   • Emphasis "what's wrong." in red
 *   • Subheadline: large grey, satisFIND® with ®
 *   • White background, lots of breathing room
 *   • Primary CTA: satisFIND® navy; Secondary: outline
 */
export default function HomePage() {
  return (
    <div className="min-h-screen bg-white text-[#0F172A]">
      <TopHeader />

      <main className="max-w-[1280px] mx-auto px-6 lg:px-10 pt-32 lg:pt-48 pb-32">
        {/* Hero — DM Serif Display, single navy, generous whitespace.
            Each statement stays on ONE line at md+ (no mid-sentence wrap)
            via nowrap + a size that fits the longest of the three
            ("See what your customers see. Lead with clarity."). Mobile
            allows wrapping because that sentence cannot fit at any
            legible size on a phone. */}
        <section data-testid="home-hero">
          <h1
            className="text-[#173F73]"
            style={{
              fontFamily: `'DM Serif Display', 'Playfair Display', 'Times New Roman', Georgia, serif`,
              fontWeight: 400,
              lineHeight: 0.96,
              letterSpacing: '-2px',
            }}
          >
            <p
              className="m-0 whitespace-normal md:whitespace-nowrap"
              style={{ fontSize: 'clamp(28px, 4vw, 56px)' }}
              data-testid="home-hero-line-1"
            >
              Your customers know things you don&apos;t.
            </p>
            <p
              className="mt-14 lg:mt-20 m-0 whitespace-normal md:whitespace-nowrap"
              style={{ fontSize: 'clamp(28px, 4vw, 56px)' }}
              data-testid="home-hero-line-2"
            >
              Most businesses find out too late.
            </p>
            <p
              className="mt-14 lg:mt-20 m-0 whitespace-normal md:whitespace-nowrap"
              style={{ fontSize: 'clamp(28px, 4vw, 56px)' }}
              data-testid="home-hero-line-3"
            >
              See what your customers see. Lead with clarity.
            </p>
          </h1>

          <div className="mt-16 lg:mt-20">
            <Link
              to="/pricing"
              className="inline-flex items-center justify-center bg-[#173F73] hover:bg-[#102E56] text-white transition-colors"
              style={{
                fontFamily: 'Inter, system-ui, sans-serif',
                fontWeight: 600,
                fontSize: '18px',
                padding: '18px 34px',
                borderRadius: '12px',
              }}
              data-testid="home-cta-primary"
            >
              FIND Your Solution
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

function TopHeader() {
  return (
    <header className="w-full">
      <div className="max-w-6xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center" data-testid="home-header-logo">
          <SatisfindLogo size={140} />
        </Link>
        <nav className="flex items-center gap-2 sm:gap-4 text-sm">
          <Link to="/satisfinder" className="hidden sm:inline text-[#334155] hover:text-[#0F172A] px-3 py-2" data-testid="home-nav-satisfinder">
            satisFINDER®
          </Link>
          <Link
            to="/auth"
            className="bg-[#1A3E6F] hover:bg-[#12305A] text-white font-semibold px-4 py-2 rounded-lg text-sm"
            data-testid="home-nav-login"
          >
            Sign in
          </Link>
        </nav>
      </div>
    </header>
  );
}

function Footer() {
  return (
    <footer className="border-t border-[#E2E8F0]">
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-8 flex flex-col sm:flex-row justify-between items-center gap-3">
        <div className="flex items-center gap-3 text-xs text-[#64748B]">
          <SatisfindLogo size={80} />
          <span>© {new Date().getFullYear()} SatisFIND<sup>®</sup>. All Rights Reserved.</span>
        </div>
        <div className="flex items-center gap-4 text-xs text-[#64748B]">
          <Link to="/satisfinder" className="hover:text-[#0F172A]">satisFINDER®</Link>
          <Link to="/auth" className="hover:text-[#0F172A]">Sign in</Link>
        </div>
      </div>
    </footer>
  );
}
